// Implements: F-IDN-012
/**
 * How strong a new password looks, 0..4, as the v3 mockup scores it (`strengthOf`): one point
 * each for 8+ characters, 14+ characters, upper and lower case, and a digit or a symbol. It is a
 * hint on screen only; the server's own rules decide.
 *
 * The hint never praises what the server refuses: below `MINIMUM_PASSWORD_LENGTH` (the backend's
 * `password_policy.MINIMUM_PASSWORD_LENGTH`) a password scores the weakest level whatever it
 * mixes, and `isBelowMinimum` lets the form name it "too short" instead. Lengths are counted in
 * code points, as Python's `len()` counts them: "Contraseña😀" is 11 characters, not 12 UTF-16 units.
 *
 * The one score behind the shared `PasswordStrength` meter (the reset page and the account page) and
 * the account form's own "too short" refusal; no screen keeps a copy of it.
 */

export type Strength = 0 | 1 | 2 | 3 | 4;

/** Shortest new password the server accepts (backend `identity/domain/password_policy.py`). */
export const MINIMUM_PASSWORD_LENGTH = 12;

/** The length the server measures: code points, so an emoji is one character. */
function characters(password: string): number {
  return Array.from(password).length;
}

/** Whether a typed (non-empty) password is shorter than the server accepts. */
export function isBelowMinimum(password: string): boolean {
  return password !== "" && characters(password) < MINIMUM_PASSWORD_LENGTH;
}

export function passwordStrength(password: string): Strength {
  if (password === "") return 0;
  if (isBelowMinimum(password)) return 1;
  const length = characters(password);
  const points =
    Number(length >= 8) +
    Number(length >= 14) +
    Number(/[A-Z]/.test(password) && /[a-z]/.test(password)) +
    Number(/[\d\W_]/.test(password));
  return Math.min(4, points) as Strength;
}
